import React from "react";
import classNames from "classnames";
import { Menu } from "@design-system/Menu/Menu.lib";
import type { Align } from "@design-system/overlay/overlayPositioning";
import { ArrowDownIcon } from "@icons";
import { Button } from "./Button";
import { ButtonWithLoader } from "./ButtonWithLoader";
import type { ButtonSize } from "./Button";
import styles from "./SplitButton.module.scss";

interface SplitButtonProps {
    children: React.ReactNode;
    onPrimaryClick: () => void;
    menu: React.ReactNode;
    menuAriaLabel: string;
    variant?: "primary" | "secondary";
    size?: ButtonSize;
    menuAlign?: Align;
    /** Blocks the primary action only; gate each menu item on its own. */
    disabled?: boolean;
    loading?: boolean;
    className?: string;
}

/**
 * A primary action joined to a caret that opens related actions in a menu.
 * The caret stays enabled while the primary action is disabled or loading,
 * so a disabled menu item can still show its `disabledReason`.
 */
export const SplitButton = ({
    children,
    onPrimaryClick,
    menu,
    menuAriaLabel,
    variant = "primary",
    size = "md",
    menuAlign = "end",
    disabled = false,
    loading = false,
    className,
}: SplitButtonProps) => (
    <div className={classNames(styles.splitButton, className)}>
        <ButtonWithLoader
            variant={variant}
            size={size}
            onClick={onPrimaryClick}
            disabled={disabled}
            loading={loading}
            className={styles.primary}
        >
            {children}
        </ButtonWithLoader>
        <Menu.Root
            align={menuAlign}
            trigger={
                <Button
                    variant={variant}
                    size={size}
                    iconOnly
                    aria-label={menuAriaLabel}
                    className={styles.caret}
                >
                    <ArrowDownIcon />
                </Button>
            }
        >
            {menu}
        </Menu.Root>
    </div>
);
